import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    ReactiveFormsModule,
    NzInputModule,
    NzButtonModule,
    TranslateModule,
    NzIconModule
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.less'
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  resetToken!: string;
  passwordVisible: boolean = false;
  passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Initialize form with custom password strength validator
    this.resetPasswordForm = this.fb.group({
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          this.passwordStrengthValidator,
          this.passwordByteLengthValidator,
        ],
      ],
      confirmPassword: ['', Validators.required],
    }, { validator: this.passwordMatchValidator });

    // Get the token from the URL
    this.route.paramMap.subscribe(params => {
      this.resetToken = params.get('token') || '';
    });

    this.resetPasswordForm.get('newPassword')?.valueChanges.subscribe(value => {
      this.passwordStrength = this.evaluatePasswordStrength(value);
    });
  }

  passwordMatchValidator(form: FormGroup) {
    return form.controls['newPassword'].value === form.controls['confirmPassword'].value ? null : { mismatch: true };
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid) {
      if (this.resetPasswordForm.get('newPassword')?.errors?.['minlength']) {
        this.message.error(this.translate.instant('LOGIN.PASSWORD_TOO_SHORT'));
      }
      if (this.resetPasswordForm.get('newPassword')?.errors?.['passwordStrength']) {
        this.message.error(this.translate.instant('LOGIN.PASSWORD_WEAK'));
      }
      if (this.resetPasswordForm.get('newPassword')?.errors?.['passwordTooLong']) {
        this.message.error(this.translate.instant('LOGIN.PASSWORD_TOO_LONG'));
      }
      if (this.resetPasswordForm.getError('mismatch')) {
        this.message.error(this.translate.instant('LOGIN.PASSWORDS_DO_NOT_MATCH'));
      }
      return;
    }

    const { newPassword, confirmPassword } = this.resetPasswordForm.value;

    if (newPassword !== confirmPassword) {
      this.message.error(this.translate.instant('LOGIN.PASSWORDS_DO_NOT_MATCH'));
      return;
    }

    // Call AuthService to reset the password
    this.authService.resetPassword(this.resetToken, newPassword).subscribe({
      next: () => {
        this.message.success(this.translate.instant('LOGIN.PASSWORD_UPDATED_SUCCESS'));
        this.router.navigate(['/login']);
      },
      error: (err: Error) => {
        const msg = (err?.message || '').toLowerCase();
        let errorMessage: string;
        if (msg.includes('invalid') || msg.includes('expired')) {
          errorMessage = this.translate.instant('LOGIN.RESET_PASSWORD_INVALID_OR_EXPIRED_TOKEN');
        } else {
          errorMessage = this.translate.instant('LOGIN.ERROR_RESETTING_PASSWORD');
        }
        this.message.error(errorMessage);
      },
    });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;  // Toggle password visibility
  }

  // Custom validator for password strength
  private passwordStrengthValidator(control: AbstractControl): { passwordStrength: true } | null {
    const value = control.value || '';
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return { passwordStrength: true };
    }
    return null;
  }

  private passwordByteLengthValidator(control: AbstractControl): { passwordTooLong: true } | null {
    const password = typeof control.value === 'string' ? control.value : '';
    return new TextEncoder().encode(password).length > 72 ? { passwordTooLong: true } : null;
  }
  evaluatePasswordStrength(password: string): 'weak' | 'medium' | 'strong' | '' {
    if (!password) return '';

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /\W/.test(password);

    const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (password.length >= 8 && score >= 4) return 'strong';
    if (password.length >= 6 && score >= 2) return 'medium';
    return 'weak';
  }
}
